import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { loginSchema, type LoginInput } from '@/shared/validation/auth';
import { api, ApiError } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Alert } from '@/client/components/ui/alert';
import { Seo } from '@/client/components/Seo';
import { isLocale, type Locale } from '@/client/i18n';
import type { Me } from '@/client/hooks/useMe';

export function Login() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);
  const [evictionNotice, setEvictionNotice] = useState(false);

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  // Honour ?redirectTo when set by gated routes (Checkout, PortalLayout).
  // Constrain to local paths to prevent open-redirect; anything that
  // doesn't start with '/' falls back to the portal.
  function safeRedirectTarget(): string {
    const raw = searchParams.get('redirectTo');
    if (raw && raw.startsWith('/') && !raw.startsWith('//')) return raw;
    return `/${locale}/portal`;
  }

  async function onSubmit(values: LoginInput) {
    setServerError(null);
    setEvictionNotice(false);
    try {
      const data = await api<{ user: Me; oldestSessionEvicted: boolean }>('/api/v1/auth/login', {
        method: 'POST',
        body: values,
      });
      queryClient.setQueryData(['me'], data.user);
      if (data.oldestSessionEvicted) setEvictionNotice(true);
      navigate(safeRedirectTarget());
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setServerError(t('auth.errors_invalid_credentials'));
      } else if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setServerError(t('auth.errors_validation'));
      } else {
        setServerError(t('errors.generic'));
      }
    }
  }

  return (
    <div className="relative">
      <Seo
        title={t('seo.login_title')}
        description={t('seo.login_description')}
        locale={locale}
        index={false}
      />

      <div className="mx-auto max-w-md px-5 sm:px-6 py-12 sm:py-20">
        <div className="dr-card-strong p-8 sm:p-10 animate-fade-up">
          <p className="dr-eyebrow text-center">
            {t('auth.login_eyebrow', { defaultValue: 'Vitaj späť' })}
          </p>
          <h1 className="mt-3 font-display text-h2 text-primary-dark text-center">
            {t('auth.login_title')}
          </h1>
          <p className="mt-2 font-sans text-body text-text-secondary text-center">
            {t('auth.login_subtitle')}
          </p>

          <div className="mt-8">
            <GoogleButton href={`/api/v1/auth/oauth/google/start?locale=${locale}`}>
              {t('auth.with_google')}
            </GoogleButton>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-glass-border-strong" />
            <span className="font-sans text-caption uppercase tracking-[0.18em] text-text-muted">
              {t('common.or')}
            </span>
            <div className="h-px flex-1 bg-glass-border-strong" />
          </div>

          {serverError && (
            <Alert variant="error" className="mb-4">
              {serverError}
            </Alert>
          )}
          {evictionNotice && (
            <Alert variant="warning" className="mb-4">
              {t('auth.login_eviction_notice')}
            </Alert>
          )}

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="email">{t('auth.email_label')}</Label>
              <Input id="email" type="email" autoComplete="email" {...form.register('email')} />
              {form.formState.errors.email && (
                <p className="mt-1.5 text-caption text-error">{form.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password">{t('auth.password_label')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...form.register('password')}
              />
              {form.formState.errors.password && (
                <p className="mt-1.5 text-caption text-error">{form.formState.errors.password.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full mt-2"
              size="lg"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? t('common.loading') : t('auth.login_submit')}
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-between font-sans text-body-small">
            <Link to={`/${locale}/registracia`} className="text-primary hover:text-primary-dark transition-colors">
              {t('auth.login_no_account')}
            </Link>
            <Link to={`/${locale}/zabudnute-heslo`} className="text-primary hover:text-primary-dark transition-colors">
              {t('auth.login_forgot_password')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export function GoogleButton({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="group flex items-center justify-center gap-3 w-full h-12 rounded-pill bg-white border border-glass-border-strong font-sans text-body-small font-medium text-text-primary hover:bg-primary-50/60 hover:border-primary/30 hover:-translate-y-px transition-all shadow-glass-soft"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
          fill="#EA4335"
        />
      </svg>
      <span>{children}</span>
    </a>
  );
}
